// dataService.js (pure JS / ES module)
import companiesData from "../data/companies.json";
import localforage from "localforage";
import Papa from "papaparse";
import { decompressSync, strFromU8 } from "fflate";

/** ================================
 *  Config & simple cache
 *  ================================ */

let CACHE_DURATION = 5 * 60_000; // 5 minutes
export const setCacheTtlMs = (ms) => (CACHE_DURATION = ms);

const _cache = new Map();
const _promiseCache = new Map(); // Track in-flight promises to prevent duplicate concurrent fetches
const _promiseTimestamps = new Map(); // Track when promises were created

// Initialize localforage for IndexedDB persistence
localforage.config({
  name: 'DATboard',
  storeName: 'dataCache',
  description: 'Cached API responses'
});

// Persist cache across reloads (Memory → IndexedDB + SWR-style revalidate)
const persistGet = async (key) => {
  const mem = cget(key);
  if (mem) return mem;
  const disk = await localforage.getItem(key);
  if (disk && Date.now() - (disk.ts || 0) < CACHE_DURATION) return disk.data;
  return null;
};

const persistSet = async (key, data) => {
  cset(key, data);
  await localforage.setItem(key, { data, ts: Date.now() });
};

export const clearCache = () => {
  _cache.clear();
  _promiseCache.clear();
  localforage.clear(); // Also clear IndexedDB
};
const ckey = (name, args) => `${name}:${args ? JSON.stringify(args) : ""}`;
const cget = (key) => {
  const hit = _cache.get(key);
  if (hit && Date.now() - hit.ts < CACHE_DURATION) return hit.data;
  return null;
};
const cset = (key, data) => _cache.set(key, { data, ts: Date.now() });
export const clearCachedData = (key) => { 
  _cache.delete(key);
  _promiseCache.delete(key);
  _promiseTimestamps.delete(key);
};
// Get promise from cache or create new one
const getOrCreatePromise = (key, promiseFactory) => {
  // Check if we already have this promise in flight
  const existingPromise = _promiseCache.get(key);
  const existingTimestamp = _promiseTimestamps.get(key);
  
  if (existingPromise && existingTimestamp) {
    // Check if promise is stale (pending for more than 30 seconds)
    const age = Date.now() - existingTimestamp;
    if (age > 30000) {
      _promiseCache.delete(key);
      _promiseTimestamps.delete(key);
    } else {
      // Return existing promise but ensure errors are handled
      return existingPromise.catch(err => {
        // If promise failed, remove from cache and rethrow
        _promiseCache.delete(key);
        _promiseTimestamps.delete(key);
        throw err;
      });
    }
  }
  
  // Create new promise and cache it
  const promise = promiseFactory()
    .then(result => {
      return result;
    })
    .catch(err => {
      // Ensure we remove from cache on error
      _promiseCache.delete(key);
      _promiseTimestamps.delete(key);
      throw err;
    })
    .finally(() => {
      // Remove from promise cache when done (success or failure)
      _promiseCache.delete(key);
      _promiseTimestamps.delete(key);
    });
    
  _promiseCache.set(key, promise);
  _promiseTimestamps.set(key, Date.now());
  return promise;
};

/** ================================
 *  Query optimization helpers
 *  ================================ */
const DEFAULT_DAYS_BACK = 270; // fast first paint (reduced from 365)
const EXTENDED_DAYS_BACK = 365; // full history for background extension

const toISO = d => new Date(d).toISOString().slice(0,10);
const daysAgo = n => { const d=new Date(); d.setDate(d.getDate()-n); return toISO(d); };

// Abort stale requests (tab switches, fast nav)
const fetchWithAbort = async (url, options = {}) => {
  return await fetch(url, { 
    ...options, 
    credentials: 'include' 
  });
};

// Concurrency guard (global) - limits parallel requests
const limit = (n = 6) => {
  let active = 0;
  const queue = [];
  const next = () => {
    if (active >= n || !queue.length) return;
    active++;
    const { fn, resolve, reject } = queue.shift();
    fn()
      .then(resolve, reject)
      .finally(() => {
        active--;
        next();
      });
  };
  return (fn) => new Promise((resolve, reject) => {
    queue.push({ fn, resolve, reject });
    next();
  });
};

export const withLimit = limit(6);

// Prefetch on idle
const onIdle = (fn) => ('requestIdleCallback' in window)
  ? requestIdleCallback(fn, { timeout: 2000 })
  : setTimeout(fn, 300);

export const prefetchDashboard = () => {
  onIdle(async () => {
    const at = getAssetTickers();
    const topTickers = [...new Set([...at.bitcoin, ...at.ethereum, ...at.solana])]
      .slice(0, 10); // top tickers only
    
    // Use multi-ticker fetch instead of individual calls
    if (topTickers.length > 0) {
      fetchMultiTickerTimeSeries(topTickers, { daysBack: DEFAULT_DAYS_BACK, light: false })
        .catch(() => {}); // Don't block on background prefetch errors
    }
  });
};

/** ================================
 *  Asset tickers (memoized)
 *  ================================ */
const getAssetTickers = (() => {
  let memo = null;
  return () => {
    if (memo) return memo;
    if (!companiesData) {
      memo = { bitcoin: [], ethereum: [], solana: [] };
      return memo;
    }
    const pick = (arr) => (arr || []).filter(x => x.trackData === true).map(x => x.ticker);
    memo = {
      bitcoin: pick(companiesData.bitcoin || []),
      ethereum: pick(companiesData.ethereum || []),
      solana: pick(companiesData.solana || []),
    };
    return memo;
  };
})();

/** Helpers */
const latestPerTicker = (rows) => {
  // assumes rows sorted DESC by date
  const out = {};
  for (const r of rows) if (!out[r.ticker]) out[r.ticker] = r;
  return out;
};

/** ================================
 *  Time Series (per ticker)
 *  ================================ */
// Helper to refresh session cookie with retry logic
const refreshSession = async (retries = 2) => {
  for (let i = 0; i < retries; i++) {
    try {
      const resp = await fetch('/api/session', { 
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (resp.ok) {
        // Wait a bit to ensure cookie is set (especially important for cross-domain/Vercel)
        await new Promise(resolve => setTimeout(resolve, 100));
        return true;
      }
      
      // If not ok and not last retry, wait before retrying
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    } catch (error) {
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
  }
  return false;
};

// Wrapper for secured API calls that handles session refresh on auth errors
// Uses fetchWithAbort to handle stale requests
const fetchWithSession = async (url, options = {}) => {
  // fetchWithAbort already includes credentials, don't duplicate
  let resp = await fetchWithAbort(url, { ...options });
  
  // If session expired (401/403), refresh and retry (up to 2 times)
  if (resp.status === 401 || resp.status === 403) {
    const refreshed = await refreshSession(2);
    if (refreshed) {
      // Retry with fresh session
      resp = await fetchWithAbort(url, { ...options });
      // If still 401/403 after refresh, try one more time
      if (resp.status === 401 || resp.status === 403) {
        await new Promise(resolve => setTimeout(resolve, 200));
        resp = await fetchWithAbort(url, { ...options });
      }
    }
  }
  
  return resp;
};

// ---- CSV fetch helper (smaller payloads, gzipped) ----
const fetchCSV = async (url) => {
  // Add timeout wrapper to prevent hanging
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => reject(new Error('CSV fetch timeout after 30 seconds')), 30000);
  });
  
  const fetchPromise = async () => {
    try {
      // Add format=csv query parameter as fallback (Vercel CDN might strip/modify headers)
      const separator = url.includes('?') ? '&' : '?';
      const urlWithFormat = url.includes('format=') ? url : `${url}${separator}format=csv`;
      
      // Don't send Accept-Encoding: gzip to prevent browser/server from auto-decompressing
      const resp = await fetchWithSession(urlWithFormat, { 
        headers: { 
          'Accept': 'text/csv',
          'Accept-Encoding': 'identity' // Prevent automatic HTTP-level gzip decompression
        } 
      });
      if (!resp.ok) throw new Error(`CSV API error: ${resp.status}`);
      
      // Check Content-Type header to verify we got the expected format
      const contentType = resp.headers.get('Content-Type') || '';
      
      // CSV is returned as gzipped binary blob (application/octet-stream)
      const buf = new Uint8Array(await resp.arrayBuffer());
      
      // Check if we got valid gzip data (starts with gzip magic bytes: 0x1f 0x8b)
      if (buf.length < 2 || buf[0] !== 0x1f || buf[1] !== 0x8b) {
        // If content-type is JSON, maybe server didn't honor Accept header - try parsing as JSON
        if (contentType.includes('application/json')) {
          const text = new TextDecoder().decode(buf);
          const json = JSON.parse(text);
          return json?.data || [];
        }
        
        throw new Error('Invalid gzip format - response may not be gzipped');
      }
      
      const u8 = decompressSync(buf); // gunzip
      const csv = strFromU8(u8);
      
      if (!csv || csv.trim().length === 0) {
        return [];
      }
      
      const { data } = Papa.parse(csv, { header: true, dynamicTyping: true, skipEmptyLines: true });
      // Ensure data is always an array
      if (!Array.isArray(data)) {
        return [];
      }
      if (data.length === 0) {
        return [];
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  };
  
  return Promise.race([fetchPromise(), timeoutPromise]);
};

export const fetchTickerTimeSeries = async (ticker) => {
  const key = ckey("fetchTickerTimeSeries", ticker);
  const cached = await persistGet(key);
  if (cached) {
    // SWR: kick off background refresh but return fast
    getOrCreatePromise(key, async () => {
      try {
        const startDate = daysAgo(DEFAULT_DAYS_BACK);
        const params = new URLSearchParams({ type: 'series', table: 'daily_time_series_per_ticker_mv', ticker, gteDate: startDate, order: 'desc', limit: '50000' });
        // Use CSV format for smaller payload
        const data = await fetchCSV(`/api/db?${params.toString()}`);
        console.log('[fetchTickerTimeSeries] fetched rows', ticker, Array.isArray(data) ? data.length : 0);
        await persistSet(key, data);
        return data;
      } catch (err) {
        throw err; // Re-throw to ensure promise cache cleanup
      }
    }).catch(() => {}); // Don't block on background refresh errors
    return cached;
  }

  // Use promise cache to prevent concurrent duplicate fetches
  return getOrCreatePromise(key, async () => {
    try {
      // Route through secured API
      // Note: Don't pass light=true - we want full data (all columns including nav_usd, premium_discount, trading_volume_shares)
      const startDate = daysAgo(DEFAULT_DAYS_BACK);
      const params = new URLSearchParams({ type: 'series', table: 'daily_time_series_per_ticker_mv', ticker, gteDate: startDate, order: 'desc', limit: '50000' });
      // Don't pass light parameter - we want full columns for company detail pages
      // Use CSV format for smaller payload
      const data = await fetchCSV(`/api/db?${params.toString()}`);
      console.log('[fetchTickerTimeSeries] fetched rows', ticker, Array.isArray(data) ? data.length : 0);
      await persistSet(key, data);
      return data;
    } catch (error) {
      throw error;
    }
  });
};

// ---- NEW: multi-ticker fetch (single round-trip) ----
export const fetchMultiTickerTimeSeries = async (tickers, { daysBack = DEFAULT_DAYS_BACK, light = true } = {}) => {
  if (!tickers || tickers.length === 0) {
    return {};
  }

  const key = ckey("fetchMultiTickerTimeSeries", { tickers: tickers.sort().join(','), daysBack, light });
  const cached = await persistGet(key);
  if (cached) {
    // SWR: kick off background refresh but return fast
    getOrCreatePromise(key, async () => {
      const params = new URLSearchParams({
        type: 'series',
        table: 'daily_time_series_per_ticker_mv',
        tickers: tickers.join(','),
        order: 'desc',
        limit: '50000',
        light: String(!!light),
        multi: 'true' // tells API to group by ticker
      });
      // Use CSV format for smaller payload
      const data = await fetchCSV(`/api/db?${params.toString()}`);
      
      // Group by ticker (CSV comes back flat, need to group it)
      const grouped = {};
      for (const row of data) {
        if (!row || !row.ticker) {
          continue;
        }
        const ticker = row.ticker;
        if (!grouped[ticker]) {
          grouped[ticker] = [];
        }
        grouped[ticker].push(row);
      }
      // Ensure all requested tickers are present
      for (const t of tickers) {
        if (!grouped[t]) {
          grouped[t] = [];
        }
      }
      await persistSet(key, grouped);
      return grouped;
    }).catch(() => {}); // Don't block on background refresh errors
    return cached;
  }

  return getOrCreatePromise(key, async () => {
    try {
      const params = new URLSearchParams({
        type: 'series',
        table: 'daily_time_series_per_ticker_mv',
        tickers: tickers.join(','),
        order: 'desc',
        limit: '50000',
        light: String(!!light),
        multi: 'true'
      });
      const url = `/api/db?${params.toString()}`;
      
      // Use CSV format for smaller payload
      const data = await fetchCSV(url);
      console.log('[fetchMultiTickerTimeSeries] result', {
        tickers: tickers.join(','),
        light,
        rows: Array.isArray(data) ? data.length : 0,
        sampleStart: Array.isArray(data) ? data.slice(0, 2) : [],
        sampleEnd: Array.isArray(data) ? data.slice(-2) : []
      });
      
      // Group by ticker (CSV comes back flat, need to group it)
      const grouped = {};
      for (const row of data) {
        if (!row || !row.ticker) {
          continue;
        }
        const ticker = row.ticker;
        if (!grouped[ticker]) {
          grouped[ticker] = [];
        }
        grouped[ticker].push(row);
      }
      // Ensure all requested tickers are present
      for (const t of tickers) {
        if (!grouped[t]) {
          grouped[t] = [];
        }
      }
      await persistSet(key, grouped);
      return grouped;
    } catch (error) {
      throw error;
    }
  });
};

// Lazy-extend to full history (365 days) in background
export const warmExtendSeries = async (ticker) => {
  const key = ckey("fetchTickerTimeSeries", ticker);
  await getOrCreatePromise(`${key}:extend`, async () => {
    const params = new URLSearchParams({ 
      type: 'series', 
      ticker,
      table: 'daily_time_series_per_ticker_mv',
      gteDate: daysAgo(EXTENDED_DAYS_BACK), 
      order: 'desc', 
      limit: '50000' 
    });
    // Use CSV format for smaller payload
    const data = await fetchCSV(`/api/db?${params.toString()}`);
    await persistSet(key, data);
  });
};

export const fetchTickerProFormaTimeSeries = async (ticker) => {
  const key = ckey("fetchTickerProFormaTimeSeries", ticker);
  const cached = await persistGet(key);
  if (cached) {
    // Don't trigger background refresh - data is loaded on-demand when company is clicked
    return cached;
  }

  return getOrCreatePromise(key, async () => {
    try {
      const startDate = daysAgo(DEFAULT_DAYS_BACK);
      const params = new URLSearchParams({ 
        type: 'proforma_series',
        table: 'daily_time_series_per_ticker_proforma_mv',
        ticker,
        gteDate: startDate,
        order: 'desc',
        limit: String(50000)
      });
      const data = await fetchCSV(`/api/db?${params.toString()}`);
      await persistSet(key, data);
      return data;
    } catch (error) {
      throw error;
    }
  });
};

export const fetchTickerGrowthDriversTimeSeries = async (ticker) => {
  const key = ckey("fetchTickerGrowthDriversTimeSeries", ticker);
  const cached = await persistGet(key);
  if (cached) {
    // Don't trigger background refresh - data is loaded on-demand when company is clicked
    return cached;
  }

  return getOrCreatePromise(key, async () => {
    try {
      // Get full dataset - no date truncation
      // Try using 'growth_drivers' type (without _series suffix)
      const params = new URLSearchParams({ 
        type: 'growth_drivers', 
        table: 'daily_time_series_growth_drivers_per_ticker_mv',
        ticker, 
        order: 'desc', 
        limit: String(50000) 
      });
      const data = await fetchCSV(`/api/db?${params.toString()}`);
      await persistSet(key, data);
      return data;
    } catch (error) {
      throw error;
    }
  });
};

/** ================================
 *  Tickers Time Series by Asset (batched parallel requests)
 *  ================================ */
export const fetchTickersTimeSeriesByAsset = async (assetType, onProgress = null) => {
  const key = ckey("fetchTickersTimeSeriesByAsset", assetType);
  const cached = cget(key);
  if (cached) return cached;

  // Get tickers for this specific asset
  const at = getAssetTickers();
  const tickers = assetType === 'bitcoin' ? at.bitcoin :
                  assetType === 'ethereum' ? at.ethereum :
                  assetType === 'solana' ? at.solana : [];

  if (!tickers || tickers.length === 0) {
    cset(key, []);
    return [];
  }

  // Report initial progress
  if (onProgress) {
    onProgress(10);
  }

  try {
    // Determine batching strategy (bitcoin: two batches to avoid row limits)
    const batches = (() => {
      if (assetType === 'bitcoin' && tickers.length > 0) {
        const mid = Math.ceil(tickers.length / 2);
        return [tickers.slice(0, mid), tickers.slice(mid)].filter(batch => batch.length > 0);
      }
      return [tickers];
    })();

    const multiResult = {};
    let completedBatches = 0;

    await Promise.all(batches.map(async (batchTickers) => {
      if (!batchTickers.length) return;

      const batchResult = await fetchMultiTickerTimeSeries(batchTickers, { daysBack: DEFAULT_DAYS_BACK, light: false });
      for (const ticker of batchTickers) {
        multiResult[ticker] = batchResult[ticker] || [];
      }

      if (onProgress) {
        completedBatches += 1;
        const batchProgress = 10 + Math.round((completedBatches / batches.length) * 80);
        onProgress(Math.min(90, batchProgress));
      }
    }));
    
    // Ensure we hit the 90% progress mark after batching
    if (onProgress) onProgress(90);

    // Convert grouped result { [ticker]: Row[] } to flat array
    const allData = [];
    for (const ticker of tickers) {
      const tickerData = multiResult[ticker] || [];
      allData.push(...tickerData);
    }
    
    if (allData.length === 0) {
      cset(key, []);
      return [];
    }
    
    // Verify we only have data for the requested asset's tickers
    const tickerSet = new Set(tickers);
    const filteredData = allData.filter(row => tickerSet.has(row.ticker));

    // Only interpolate columns that exist in lightweight mode
    const processedData = await interpolateWeekendData(filteredData, ["mcap_estimated", "turnover", "crypto_per_share", "outstanding_shares_estimated", "mnav", "crypto_holdings"]);
    
    // Report complete
    if (onProgress) {
      onProgress(100);
    }
    
    cset(key, processedData);
    return processedData;
  } catch (error) {
    cset(key, []);
    return [];
  }
};

/** ================================
 *  All Tickers Time Series (batched parallel requests)
 *  ================================ */
export const fetchAllTickersTimeSeries = async () => {
  const key = ckey("fetchAllTickersTimeSeries");
  const cached = cget(key);
  if (cached) return cached;

  // Get all tickers
  const at = getAssetTickers();
  const allTickers = Array.from(new Set([...at.bitcoin, ...at.ethereum, ...at.solana]));

  if (!allTickers || allTickers.length === 0) {
    cset(key, []);
    return [];
  }

  try {
    // Use multi-ticker fetch - single round-trip instead of N+1 queries
    const multiResult = await fetchMultiTickerTimeSeries(allTickers, { daysBack: DEFAULT_DAYS_BACK, light: false });
    
    // Convert grouped result { [ticker]: Row[] } to flat array
    const allData = [];
    for (const ticker of allTickers) {
      const tickerData = multiResult[ticker] || [];
      allData.push(...tickerData);
    }

    cset(key, allData);
    return allData;
  } catch (error) {
    cset(key, []);
    return [];
  }
};


export const fetchAllTickers = async () => {
  const key = ckey("fetchAllTickers");
  const cached = cget(key);
  if (cached) return cached;

  // Use tracked tickers from companies.json (filtered by active tickers)
  const at = getAssetTickers();
  const tickers = Array.from(new Set([...at.bitcoin, ...at.ethereum, ...at.solana]));

  cset(key, tickers);
  return tickers;
};

export const fetchLatestTickerData = async (ticker) => {
  const key = ckey("fetchLatestTickerData", ticker);
  const cached = cget(key);
  if (cached !== null) return cached;

  // Query via secured API - use CSV format for consistency
  const params = new URLSearchParams({ type: 'series', table: 'daily_time_series_per_ticker_mv', ticker, order: 'desc', limit: '1' });
  const data = await fetchCSV(`/api/db?${params.toString()}`);
  const res = data && data.length ? data[0] : null;
  cset(key, res);
  return res;
};

export const fetchTickerDataByDateRange = async (ticker, startDate, endDate) => {
  // Query via secured API - use CSV format for better performance
  const params = new URLSearchParams({ type: 'series', table: 'daily_time_series_per_ticker_mv', ticker, gteDate: startDate, lteDate: endDate, order: 'asc', limit: '50000' });
  return await fetchCSV(`/api/db?${params.toString()}`);
};

/** ================================
 *  Filings (one builder for all)
 *  ================================ */
const _fetchFilings = async ({ ticker, form, startDate, endDate, limit } = {}) => {
  const key = ckey("_fetchFilings", { ticker, form, startDate, endDate, limit });
  const cached = cget(key);
  if (cached) return cached;

  // Use promise cache to prevent concurrent duplicate fetches
  return getOrCreatePromise(key, async () => {
    // Route through serverless endpoint to respect RLS (service key on server)
    const params = new URLSearchParams();
    if (ticker) params.set('ticker', ticker);
    if (form) params.set('form', form);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (limit) params.set('limit', String(limit));

    const url = `/api/filings?${params.toString()}`;

    // All API routes use session cookies (DATAPP_SESSION), not auth tokens
    const resp = await fetchWithSession(url);
    if (!resp.ok) {
      throw new Error(`Filings API error: ${resp.status}`);
    }
    const json = await resp.json();
    const data = json?.data || [];
    cset(key, data);
    return data;
  });
};

export const fetchRecentFilings = (limit = null) => _fetchFilings({ limit });
export const fetchFilingsByTicker = (ticker, limit = null) => _fetchFilings({ ticker, limit });
export const fetchFilingsByFormType = (formType, limit = null) => _fetchFilings({ form: formType, limit });
export const fetchFilingsByDateRange = (startDate, endDate, limit = null) =>
  _fetchFilings({ startDate, endDate, limit });

/** ================================
 *  Asset Aggregations (series table)
 *  ================================ */
export const fetchAssetHoldings = async (assetType) => {
  const key = ckey("fetchAssetHoldings", assetType);
  const cached = cget(key);
  if (cached) return cached;

  const tickers = getAssetTickers()[assetType];
  if (!tickers) throw new Error(`Unknown asset type: ${assetType}`);

  // Query via secured API - use CSV format for better performance
  const startDate = daysAgo(120);
  const params = new URLSearchParams({ 
    type: 'series',
    table: 'daily_time_series_per_ticker_mv',
    tickers: tickers.join(','), 
    gteDate: startDate, 
    order: 'desc', 
    limit: String(tickers.length * 10) // Get multiple entries per ticker to ensure we get latest
  });
  const rows = await fetchCSV(`/api/db?${params.toString()}`);

  const latest = latestPerTicker(rows);
    let totalHoldings = 0;
    const tickerHoldings = {};
  for (const t in latest) {
    const r = latest[t];
    const h = (r.crypto_per_share || 0) * (r.outstanding_shares_estimated || 0);
    tickerHoldings[t] = h; 
    totalHoldings += h;
  }
  const res = { totalHoldings, tickerHoldings, assetType };
  cset(key, res);
  return res;
};

export const fetchAssetMarketCap = async (assetType) => {
  const key = ckey("fetchAssetMarketCap", assetType);
  const cached = cget(key);
  if (cached !== null) return cached;

  const tickers = getAssetTickers()[assetType];
  if (!tickers) throw new Error(`Unknown asset type: ${assetType}`);

  // Query via secured API - use CSV format for better performance
  const startDate = daysAgo(120);
  const params = new URLSearchParams({ 
    type: 'series',
    table: 'daily_time_series_per_ticker_mv',
    tickers: tickers.join(','), 
    gteDate: startDate, 
    order: 'desc', 
    limit: String(tickers.length * 10) 
  });
  const rows = await fetchCSV(`/api/db?${params.toString()}`);
  
  // Filter out rows where mcap_estimated is null
  const rowsWithMcap = rows.filter(r => r.mcap_estimated !== null && r.mcap_estimated !== undefined && r.mcap_estimated !== 0);
  
  const latest = latestPerTicker(rowsWithMcap);
  const total = Object.values(latest).reduce((sum, r) => sum + (r.mcap_estimated || 0), 0);

  cset(key, total);
  return total;
};

export const fetchTotalMarketCap = async () => {
  const key = ckey("fetchTotalMarketCap");
  const cached = cget(key);
  if (cached !== null) return cached;

  // Check if we already have snapshot data cached - reuse it if available
  const snapshotKey = ckey("fetchLatestTickerSnapshot");
  const snapshotCached = cget(snapshotKey);
  
  if (snapshotCached && snapshotCached.length > 0) {
    // Reuse cached snapshot data - compute total market cap synchronously
    const total = snapshotCached.reduce((sum, r) => sum + (r.mcap_estimated || 0), 0);
    cset(key, total);
    return total;
  }

  // Otherwise, fetch snapshot (but this should rarely happen as snapshot is usually fetched first)
  const at = getAssetTickers();
  const allTickers = [...at.bitcoin, ...at.ethereum, ...at.solana];

  // Query snapshot via secured API - use CSV format for consistency
  const params = new URLSearchParams({ type: 'snapshot', tickers: allTickers.join(','), limit: '1000' });
  const data = await fetchCSV(`/api/db?${params.toString()}`);
  
  const total = (data || []).reduce((sum, r) => sum + (r.mcap_estimated || 0), 0);
  cset(key, total);
  return total;
};

/** Interpolation - uses Web Worker for large sets to avoid blocking main thread */
let interpolationWorker = null;
const getInterpolationWorker = () => {
  if (!interpolationWorker && typeof Worker !== 'undefined') {
    try {
      interpolationWorker = new Worker('/interpolationWorker.js');
    } catch (e) {
      // Fallback to main thread if worker creation fails
    }
  }
  return interpolationWorker;
};

export const interpolateWeekendData = async (data, valueFields = ["btc_mcap", "eth_mcap", "sol_mcap"]) => {
  if (!data || data.length < 2) return Promise.resolve(data);
  
  // Use worker for large datasets (>500 rows) to avoid blocking main thread
  const USE_WORKER_THRESHOLD = 500;
  if (data.length > USE_WORKER_THRESHOLD) {
    const worker = getInterpolationWorker();
    if (worker) {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Interpolation worker timeout'));
        }, 10000);
        
        worker.onmessage = (e) => {
          clearTimeout(timeout);
          resolve(e.data);
        };
        
        worker.onerror = (e) => {
          clearTimeout(timeout);
          reject(e);
        };
        
        worker.postMessage({ data, valueFields });
      });
    }
  }
  
  // Fallback to synchronous for small datasets or if worker unavailable
  // Wrap in Promise.resolve for consistent API
  const iso = (d) => new Date(d).toISOString().split("T")[0];

  // Fill zeros between prev/next
  const filled = data.map(d => ({ ...d }));
  for (let i = 0; i < filled.length; i++) {
    const cur = filled[i];
    if (valueFields.some(f => cur[f] === 0)) {
      let prev = null, next = null;

      for (let j = i - 1; j >= 0; j--) {
        if (valueFields.some(f => (filled[j][f] || 0) > 0)) { prev = filled[j]; break; }
      }
      for (let j = i + 1; j < filled.length; j++) {
        if (valueFields.some(f => (filled[j][f] || 0) > 0)) { next = filled[j]; break; }
      }
      if (prev && next) {
        const pd = new Date(prev.date).valueOf();
        const nd = new Date(next.date).valueOf();
        const cd = new Date(cur.date).valueOf();
        const ratio = nd === pd ? 0 : (cd - pd) / (nd - pd);
        for (const f of valueFields) cur[f] = Math.round(prev[f] + (next[f] - prev[f]) * ratio);
      }
    }
  }

  // Insert missing days
  const out = [];
  for (let i = 0; i < filled.length - 1; i++) {
    const a = filled[i];
    const b = filled[i + 1];
    const da = new Date(a.date);
    const db = new Date(b.date);
    const diff = Math.floor((+db - +da) / 86400000);

    out.push(a);
    if (diff > 1) {
      for (let d = 1; d < diff; d++) {
        const dt = new Date(+da + d * 86400000);
        const ratio = d / diff;
        const row = { date: iso(dt) };
        for (const f of valueFields) row[f] = Math.round(a[f] + (b[f] - a[f]) * ratio);
        out.push(row);
      }
    }
  }
  out.push(filled[filled.length - 1]);
  return Promise.resolve(out);
};

export const fetchMarketCapByAsset = async () => {
  const key = ckey("fetchMarketCapByAsset");
  const cached = cget(key);
  if (cached) return cached;

  const at = getAssetTickers();
  const allTickers = [...at.bitcoin, ...at.ethereum, ...at.solana];
  const startDate = daysAgo(DEFAULT_DAYS_BACK);

  // Query via secured API - use CSV format for better performance
  const params = new URLSearchParams({ 
    type: 'series', 
    table: 'daily_time_series_per_ticker_mv',
    tickers: allTickers.join(','), 
    gteDate: startDate, 
    order: 'asc', 
    limit: '50000' 
  });
  const data = await fetchCSV(`/api/db?${params.toString()}`);

  const byDate = {};
  for (const r of data || []) {
    const d = r.date;
    if (!byDate[d]) byDate[d] = { date: d, btc_mcap: 0, eth_mcap: 0, sol_mcap: 0 };
    if (at.bitcoin.includes(r.ticker))      byDate[d].btc_mcap += r.mcap_estimated || 0;
    else if (at.ethereum.includes(r.ticker)) byDate[d].eth_mcap += r.mcap_estimated || 0;
    else if (at.solana.includes(r.ticker))   byDate[d].sol_mcap += r.mcap_estimated || 0;
  }

  let result = Object.values(byDate).sort((a,b)=> +new Date(a.date) - +new Date(b.date));
  result = await interpolateWeekendData(result, ["btc_mcap","eth_mcap","sol_mcap"]);
  cset(key, result);
    return result;
};

export const fetchTradingVolumeByAsset = async () => {
  const key = ckey("fetchTradingVolumeByAsset");
  const cached = cget(key);
  if (cached) return cached;

  const at = getAssetTickers();
  const allTickers = [...at.bitcoin, ...at.ethereum, ...at.solana];
  const startDate = daysAgo(DEFAULT_DAYS_BACK);

  // Query via secured API - use CSV format for better performance
  const params = new URLSearchParams({ 
    type: 'series', 
    table: 'daily_time_series_per_ticker_mv',
    tickers: allTickers.join(','), 
    gteDate: startDate, 
    order: 'asc', 
    limit: '50000' 
  });
  const data = await fetchCSV(`/api/db?${params.toString()}`);

  const byDate = {};
  for (const r of data || []) {
    const d = r.date;
    if (!byDate[d]) byDate[d] = { date: d, btc_volume: 0, eth_volume: 0, sol_volume: 0 };
    if (at.bitcoin.includes(r.ticker))      byDate[d].btc_volume += r.trading_volume_shares || 0;
    else if (at.ethereum.includes(r.ticker)) byDate[d].eth_volume += r.trading_volume_shares || 0;
    else if (at.solana.includes(r.ticker))   byDate[d].sol_volume += r.trading_volume_shares || 0;
  }

  let result = Object.values(byDate)
    .sort((a,b)=>+new Date(a.date) - +new Date(b.date))
    .map(row => {
      const total = (row.btc_volume||0)+(row.eth_volume||0)+(row.sol_volume||0);
      if (!total) return { ...row, btc_volume: 0, eth_volume: 0, sol_volume: 0 };
        return {
          ...row,
        btc_volume: Math.round(((row.btc_volume/total)*100 + Number.EPSILON)*100)/100,
        eth_volume: Math.round(((row.eth_volume/total)*100 + Number.EPSILON)*100)/100,
        sol_volume: Math.round(((row.sol_volume/total)*100 + Number.EPSILON)*100)/100,
      };
    });
    
  result = await interpolateWeekendData(result, ["btc_volume","eth_volume","sol_volume"]);
  cset(key, result);
    return result;
};

/** ================================
 *  Holdings → USD (placeholder prices)
 *  ================================ */
export const fetchCryptoHoldingsUSD = async () => {
  const key = ckey("fetchCryptoHoldingsUSD");
  const cached = cget(key);
  if (cached) return cached;

  const [btc, eth, sol] = await Promise.all([
    fetchAssetHoldings("bitcoin"),
    fetchAssetHoldings("ethereum"),
    fetchAssetHoldings("solana"),
  ]);

  const prices = { BTC: 50000, ETH: 3000, SOL: 100 }; // TODO: replace with real prices
  const btcUSD = (btc.totalHoldings || 0) * prices.BTC;
  const ethUSD = (eth.totalHoldings || 0) * prices.ETH;
  const solUSD = (sol.totalHoldings || 0) * prices.SOL;
  const totalUSD = btcUSD + ethUSD + solUSD || 1;

  const res = {
      totalUSD,
    bitcoin: { holdings: btc.totalHoldings, usdValue: btcUSD, percentage: (btcUSD / totalUSD) * 100 },
    ethereum: { holdings: eth.totalHoldings, usdValue: ethUSD, percentage: (ethUSD / totalUSD) * 100 },
    solana: { holdings: sol.totalHoldings, usdValue: solUSD, percentage: (solUSD / totalUSD) * 100 },
  };

  cset(key, res);
  return res;
};

/** ================================
 *  Supply
 *  ================================ */
export const fetchCryptoSupply = async (asset) => {
  // Query via secured API - use CSV format for better performance
  const params = new URLSearchParams({ type: 'supply', asset, order: 'desc', limit: '50000' });
  return await fetchCSV(`/api/db?${params.toString()}`);
};

export const fetchLatestCryptoSupply = async (asset) => {
  const key = ckey("fetchLatestCryptoSupply", asset);
  const cached = cget(key);
  if (cached !== null) return cached;

  // Query via secured API - use CSV format for consistency
  const params = new URLSearchParams({ type: 'supply', asset, order: 'desc', limit: '1' });
  const data = await fetchCSV(`/api/db?${params.toString()}`);
  const res = data && data.length ? data[0] : null;
  cset(key, res);
  return res;
};

export const fetchCryptoSupplyByDateRange = async (asset, startDate, endDate) => {
  // Query via secured API - use CSV format for better performance
  const params = new URLSearchParams({ type: 'supply', asset, gteDate: startDate, lteDate: endDate, order: 'desc', limit: '50000' });
  return await fetchCSV(`/api/db?${params.toString()}`);
};

export const fetchAllCryptoAssets = async () => {
  const key = ckey("fetchAllCryptoAssets");
  const cached = cget(key);
  if (cached) return cached;

  // Query via secured API - use CSV format for consistency
  const params = new URLSearchParams({ type: 'supply', order: 'desc', limit: '100' });
  const data = await fetchCSV(`/api/db?${params.toString()}`);
  const uniq = Array.from(new Set((data || []).map(d => d.asset)));
  cset(key, uniq);
  return uniq;
};

export const fetchLatestCryptoSupplyByTicker = async (ticker) => {
  const key = ckey("fetchLatestCryptoSupplyByTicker", ticker);
  const cached = cget(key);
  if (cached !== null) return cached;

  // Query via secured API - use CSV format for consistency
  const params = new URLSearchParams({ type: 'supply', ticker, order: 'desc', limit: '1' });
  const data = await fetchCSV(`/api/db?${params.toString()}`);
  const res = data && data.length ? data[0] : null;
  cset(key, res);
  return res;
};

export const fetchLatestCryptoSupplyByTickerAndAsset = async (ticker, asset) => {
  const key = ckey("fetchLatestCryptoSupplyByTickerAndAsset", { ticker, asset });
  const cached = cget(key);
  if (cached !== null) return cached;

  // Use promise cache to prevent concurrent duplicate fetches
  return getOrCreatePromise(key, async () => {
    // First, check if we already have this data from fetchAllSupplyData
    const allDataKey = ckey("fetchAllSupplyData");
    const allDataCached = cget(allDataKey);
    
    if (allDataCached && allDataCached.assetTotals) {
      // Map asset code to asset type name
      const assetTypeMap = {
        'BTC': 'bitcoin',
        'ETH': 'ethereum',
        'SOL': 'solana'
      };
      
      const assetType = assetTypeMap[asset];
      if (assetType && allDataCached.assetTotals[assetType]) {
        const tickerPercentage = allDataCached.assetTotals[assetType].tickerPercentages[ticker];
        
        // Also check if we have the full row data in latestData
        // We need to reconstruct the row structure that matches the API response
        if (tickerPercentage !== undefined) {
          // Find the original row from latestData if available
          // Since fetchAllSupplyData stores latestData, we can reconstruct
          const res = {
            ticker: ticker,
            asset: asset,
            pct_of_circulating: tickerPercentage
          };
          cset(key, res);
          return res;
        }
      }
    }
    
    // If not in cache, fetch from API
    // Query via secured API - get latest entry for ticker and asset from latest table - use CSV format
    const params = new URLSearchParams({ type: 'supply', ticker, asset, table: 'company_supply_share_latest', limit: '1' });
    const data = await fetchCSV(`/api/db?${params.toString()}`);
    const res = data && data.length ? data[0] : null;
    cset(key, res);
    return res;
  });
};

export const fetchAssetSupplyPercentages = async (assetType) => {
  const key = ckey("fetchAssetSupplyPercentages", assetType);
  const cached = cget(key);
  if (cached) return cached;

  const tickers = getAssetTickers()[assetType];
  if (!tickers || tickers.length === 0) {
    const res = { totalPercentage: 0, tickerPercentages: {}, assetType };
    cset(key, res);
    return res;
  }

  // Check if underlying data is already cached - avoid async call if possible
  const allDataKey = ckey("fetchAllSupplyData");
  const allDataCached = cget(allDataKey);
  if (allDataCached) {
    // Data is already available, extract synchronously
    const assetData = allDataCached.assetTotals[assetType] || { totalPercentage: 0, tickerPercentages: {} };
    const res = { ...assetData, assetType };
    cset(key, res);
    return res;
  }

  // Otherwise, fetch (will use promise cache if already in flight)
  const allData = await fetchAllSupplyData();
  const assetData = allData.assetTotals[assetType] || { totalPercentage: 0, tickerPercentages: {} };
  const res = { ...assetData, assetType };
  
  cset(key, res);
  return res;
};

export const fetchAllSupplyData = async () => {
  const key = ckey("fetchAllSupplyData");
  const cached = cget(key);
  if (cached) return cached;

  // Use promise cache to prevent concurrent fetches
  return getOrCreatePromise(key, async () => {
    const assetMap = {
      'bitcoin': 'BTC',
      'ethereum': 'ETH',
      'solana': 'SOL'
    };

    // Query 1: Fetch latest ticker-level data from company_supply_share_latest for pie chart breakdown
    // Query ALL assets in parallel instead of sequentially for faster loading - use CSV format
    const latestDataPromises = ['bitcoin', 'ethereum', 'solana'].map(async (assetType) => {
      const asset = assetMap[assetType];
      const latestParams = new URLSearchParams({ 
        type: 'supply', 
        table: 'company_supply_share_latest',
        asset: asset
      });
      return await fetchCSV(`/api/db?${latestParams.toString()}`);
    });
    
    // Execute all asset queries in parallel
    const latestDataArrays = await Promise.all(latestDataPromises);
    const latestData = latestDataArrays.flat();

    // Query 2: Fetch daily time series data from 365 days ago onwards for charts (using aggregated table)
    const startDate = daysAgo(DEFAULT_DAYS_BACK);
    const BATCH_SIZE = 50000; // Increased from 10000 for faster loading - fetch more data per request
    let dailyData = [];
    let offset = 0;
    let hasMore = true;

    while (hasMore) {
      const dailyParams = new URLSearchParams({ 
        type: 'supply', 
        table: 'company_supply_share', // Use aggregated table
        gteDate: startDate,
        order: 'asc', 
        limit: String(BATCH_SIZE),
        offset: String(offset)
      });
      
      const batchData = await fetchCSV(`/api/db?${dailyParams.toString()}`);
      
      if (batchData.length === 0) {
        hasMore = false;
      } else {
        dailyData.push(...batchData);
        offset += batchData.length;
        
        // If we got fewer rows than requested, we've reached the end
        if (batchData.length < BATCH_SIZE) {
          hasMore = false;
        }
      }
    }

    // Process daily time series data: data is already aggregated by date and asset in company_supply_share
    // Just need to pivot by date and asset
    const dailyByDate = {};
    for (const row of dailyData) {
      const date = row.date;
      if (!date) continue;
      
      if (!dailyByDate[date]) {
        dailyByDate[date] = { date, btc_supply: 0, eth_supply: 0, sol_supply: 0 };
      }
      
      const pct = row.pct_of_circulating || 0;
      if (row.asset === 'BTC') dailyByDate[date].btc_supply = pct;
      else if (row.asset === 'ETH') dailyByDate[date].eth_supply = pct;
      else if (row.asset === 'SOL') dailyByDate[date].sol_supply = pct;
    }

    const supplyDaily = Object.values(dailyByDate).sort((a, b) => new Date(a.date) - new Date(b.date));

    // Process asset-level percentages (latest totals per asset) from Query 1
    // Data is already grouped by asset from company_supply_share_latest queries
    const assetTotals = {};
    const at = getAssetTickers(); // Still need this for ticker breakdown in pie chart
    
    for (const assetType of ['bitcoin', 'ethereum', 'solana']) {
      const assetValue = assetMap[assetType];
      
      // Filter latestData for this asset and sum pct_of_circulating
      const assetRows = latestData.filter(row => row.asset === assetValue);
      
      // Sum total percentage for this asset
      const total = assetRows.reduce((sum, row) => sum + (row.pct_of_circulating || 0), 0);
      
      // Build ticker breakdown (only for tickers in companies.json)
      const tickers = at[assetType];
      const byTicker = {};
      for (const ticker of tickers) {
        const row = assetRows.find(r => r.ticker === ticker);
        if (row) {
          byTicker[ticker] = row.pct_of_circulating || 0;
        }
      }
      
      assetTotals[assetType] = { totalPercentage: total, tickerPercentages: byTicker };
    }

    const result = {
      supplyDaily: supplyDaily,
      assetTotals: assetTotals
    };
    
    cset(key, result);
    return result;
  });
};

export const fetchDailySupplyPercentages = async () => {
  const key = ckey("fetchDailySupplyPercentages");
  const cached = cget(key);
  if (cached) return cached;

  // Check if underlying data is already cached - avoid async call if possible
  const allDataKey = ckey("fetchAllSupplyData");
  const allDataCached = cget(allDataKey);
  if (allDataCached) {
    // Data is already available, extract synchronously
    cset(key, allDataCached.supplyDaily);
    return allDataCached.supplyDaily;
  }

  // Otherwise, fetch (will use promise cache if already in flight)
  const allData = await fetchAllSupplyData();
  cset(key, allData.supplyDaily);
  return allData.supplyDaily;
};

/** ================================
 *  Rankings & Growth
 *  ================================ */


export const fetchDailyGrowthData = async () => {
  const key = ckey("fetchDailyGrowthData");
  const cached = cget(key);
  if (cached) return cached;

  // Use promise cache to prevent concurrent fetches (this query is expensive!)
  return getOrCreatePromise(key, async () => {
    const at = getAssetTickers();
    const allTickers = [...at.bitcoin, ...at.ethereum, ...at.solana];
    const startDate = daysAgo(DEFAULT_DAYS_BACK);

    // Query via secured API - use CSV format for better performance
    const params = new URLSearchParams({ type: 'growth', tickers: allTickers.join(','), gteDate: startDate, limit: '50000' });
    const data = await fetchCSV(`/api/db?${params.toString()}`);

    cset(key, data);
    return data;
  });
};

export const getLatestStockRankingsByTicker = async () => {
  const key = ckey("getLatestStockRankingsByTicker");
  const cached = cget(key);
  if (cached) return cached;

  // Use promise cache to prevent concurrent fetches
  return getOrCreatePromise(key, async () => {
    // Query rankings to find neighbors (companies ranked above/below DAT leaders)
    // Since rankings are global (all companies), we need a subset to find neighbors
    // Limit to top 2000 companies - DAT companies are typically ranked in top 1000,
    // so 2000 gives us enough buffer to find neighbors ranked above/below
    const startDate = daysAgo(DEFAULT_DAYS_BACK);
    // Use CSV format for smaller payload
    const params = new URLSearchParams({ type: 'rankings', gteDate: startDate, limit: '2000' });
    const data = await fetchCSV(`/api/db?${params.toString()}`);
    const latest = latestPerTicker(data || []);
    cset(key, latest);
    return latest;
  });
};

export const getTradingVolumeRankingsLeaderboard = async () => {
  const latest = await getLatestStockRankingsByTicker();
  return Object.values(latest)
    .map(r => ({
      ticker: r.ticker,
      dollar_volume: r.dollar_volume || 0,
      rank_dollar_volume: r.rank_dollar_volume || 0,
      todays_change_perc: r.todays_change_perc || 0,
      rank_change_perc: r.rank_change_perc || 0,
      is_dat: r.is_dat || false,
      date: r.date,
    }))
    .sort((a, b) => (a.rank_dollar_volume || 0) - (b.rank_dollar_volume || 0));
};

export const getDATLeaders = async () => {
  const key = ckey("getDATLeaders");
  const cached = cget(key);
  if (cached) return cached;

  const latest = await getLatestStockRankingsByTicker();
  const rows = Object.values(latest);
  const dat = rows.filter(r => r.is_dat === true);

  const pickMinBy = (arr, field) =>
    arr.reduce((best, cur) => (!best || (cur[field] || Infinity) < (best[field] || Infinity) ? cur : best), null);

  const tradingVolumeLeader = pickMinBy(dat, "rank_dollar_volume");
  const transactionCountLeader = pickMinBy(dat, "rank_transactions");
  const priceChangeLeader = dat.reduce(
    (best, cur) => (!best || (cur.todays_change_perc || -Infinity) > (best.todays_change_perc || -Infinity) ? cur : best),
    null
  );

  // Find companies near a given rank (handles gaps in rankings)
  // Allow any company (DAT or non-DAT) to be a neighbor
  // leader is optional - exclude from search if provided
  const findByRank = (field, base, delta, excludeLeader = null) => {
    if (!base) return null;
    const targetRank = base + delta;
    
    // Filter: exclude the leader and must have valid field value
    let candidates = rows.filter(r => {
      const hasValidField = r[field] !== null && r[field] !== undefined;
      const notExcluded = !excludeLeader || r.ticker !== excludeLeader.ticker;
      return hasValidField && notExcluded;
    });
    
    // First try exact match
    const exactMatch = candidates.find(r => r[field] === targetRank);
    if (exactMatch) return exactMatch;
    
    // If no exact match, find the closest rank
    const sorted = candidates
      .sort((a, b) => Math.abs(a[field] - targetRank) - Math.abs(b[field] - targetRank));
    
    return sorted[0] || null;
  };

    const result = {
      tradingVolumeLeader,
      transactionCountLeader,
    tradingVolumeRunnerUp: findByRank("rank_dollar_volume", tradingVolumeLeader?.rank_dollar_volume, +1, tradingVolumeLeader),
    transactionCountRunnerUp: findByRank("rank_transactions", transactionCountLeader?.rank_transactions, +1, transactionCountLeader),
    tradingVolumeTemp: findByRank("rank_dollar_volume", tradingVolumeLeader?.rank_dollar_volume, -1, tradingVolumeLeader),
    transactionCountTemp: findByRank("rank_transactions", transactionCountLeader?.rank_transactions, -1, transactionCountLeader),
      priceChangeLeader,
    priceChangeRunnerUp: findByRank("rank_change_perc", priceChangeLeader?.rank_change_perc, +1, priceChangeLeader),
    priceChangeTemp: findByRank("rank_change_perc", priceChangeLeader?.rank_change_perc, -1, priceChangeLeader),
    allDATCompanies: dat,
  };

  cset(key, result);
    return result;
};

export const submitTicker = async (ticker) => {
  if (!ticker || typeof ticker !== 'string') {
    throw new Error('Invalid ticker');
  }

  const resp = await fetchWithSession('/api/db', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      type: 'ticker_submission',
      ticker: ticker.trim().toUpperCase()
    })
  });

  if (!resp.ok) {
    const errorData = await resp.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `API error: ${resp.status}`);
  }

  const data = await resp.json();
  return data;
};

export const fetchLatestTickerSnapshot = async () => {
  const key = ckey("fetchLatestTickerSnapshot");
  const cached = cget(key);
  if (cached) return cached;

  // Use promise cache to prevent concurrent fetches
  return getOrCreatePromise(key, async () => {
    const at = getAssetTickers();
    const allTickers = [...at.bitcoin, ...at.ethereum, ...at.solana];

    // Check if we already have latest rankings data cached - reuse it if available
    const latestRankingsKey = ckey("getLatestStockRankingsByTicker");
    const latestRankingsCached = cget(latestRankingsKey);
    
    let latestRankings = {};
    if (latestRankingsCached) {
      // We already have latest rankings data - extract just the DAT tickers we need
      latestRankings = {};
      for (const ticker of allTickers) {
        if (latestRankingsCached[ticker]) {
          latestRankings[ticker] = latestRankingsCached[ticker];
        }
      }
    } else {
      // Query snapshot and rankings in parallel via secured API - use CSV format
      const snapshotParams = new URLSearchParams({ type: 'snapshot', tickers: allTickers.join(','), limit: '1000' });
      const rankingsParams = new URLSearchParams({ type: 'rankings', tickers: allTickers.join(','), limit: '1000' });
      
      const [snapshotData, rankingsData] = await Promise.all([
        fetchCSV(`/api/db?${snapshotParams.toString()}`),
        fetchCSV(`/api/db?${rankingsParams.toString()}`)
      ]);

      latestRankings = latestPerTicker(rankingsData || []);
      const mergedData = (snapshotData || []).map(row => ({
        ...row,
        rank_dollar_volume: latestRankings[row.ticker]?.rank_dollar_volume || null,
        rank_transactions: latestRankings[row.ticker]?.rank_transactions || null,
      }));

      cset(key, mergedData);
      return mergedData;
    }

    // If we reused cached rankings, we still need to fetch snapshot - use CSV format
    const snapshotParams = new URLSearchParams({ type: 'snapshot', tickers: allTickers.join(','), limit: '1000' });
    const snapshot = await fetchCSV(`/api/db?${snapshotParams.toString()}`);

    const mergedData = (snapshot || []).map(row => ({
      ...row,
      rank_dollar_volume: latestRankings[row.ticker]?.rank_dollar_volume || null,
      rank_transactions: latestRankings[row.ticker]?.rank_transactions || null,
    }));

    cset(key, mergedData);
    return mergedData;
  });
};

export const fetchLatestTimeSeriesSnapshot = async () => {
  const key = ckey("fetchLatestTimeSeriesSnapshot");
  const cached = cget(key);
  if (cached) return cached;

  // Use promise cache to prevent concurrent fetches
  return getOrCreatePromise(key, async () => {
    // Query latest time series snapshot via secured API - use CSV format
    const params = new URLSearchParams({ type: 'latest_series', limit: '50000' });
    const url = `/api/db?${params.toString()}`;

    console.log('[fetchLatestTimeSeriesSnapshot] Requesting latest_series snapshot', {
      tickerCount: 'all',
      url
    });

    try {
      const data = await fetchCSV(url);

      console.log('[fetchLatestTimeSeriesSnapshot] Received latest_series snapshot', {
        rows: Array.isArray(data) ? data.length : 0
      });

      cset(key, data);
      return data;
    } catch (error) {
      console.error('[fetchLatestTimeSeriesSnapshot] Failed to fetch latest_series snapshot', error);
      throw error;
    }
  });
};

/** ================================
 *  Unified Growth Asset Data Fetch
 *  ================================ */
const fetchAllGrowthAssetData = async () => {
  const key = ckey("fetchAllGrowthAssetData");
  const cached = cget(key);
  if (cached) return cached;

  // Use promise cache to prevent concurrent fetches
  return getOrCreatePromise(key, async () => {
    // Query via secured API - get growth_asset data once (all columns)
    const params = new URLSearchParams({ type: 'growth_asset', order: 'desc', limit: '1000' });
    const data = await fetchCSV(`/api/db?${params.toString()}`);
    console.log('[fetchAllGrowthAssetData] fetched rows', Array.isArray(data) ? data.length : 0);
    cset(key, data);
    return data;
  });
};

export const fetchNAVGrowthByAsset = async () => {
  const key = ckey("fetchNAVGrowthByAsset");
  const cached = cget(key);
  if (cached) return cached;

  // Check if underlying data is already cached - avoid async call if possible
  const allDataKey = ckey("fetchAllGrowthAssetData");
  const allDataCached = cget(allDataKey);
  const data = allDataCached ? allDataCached : await fetchAllGrowthAssetData();

  // Transform to chart format: { date, btc_nav_growth, eth_nav_growth, sol_nav_growth }
  const byDate = {};
  for (const r of data || []) {
    const d = r.date;
    if (!byDate[d]) byDate[d] = { date: d, btc_nav_growth: 0, eth_nav_growth: 0, sol_nav_growth: 0 };
    
    const assetKey = r.asset === 'BTC' ? 'btc_nav_growth' : 
                     r.asset === 'ETH' ? 'eth_nav_growth' : 
                     r.asset === 'SOL' ? 'sol_nav_growth' : null;
    
    if (assetKey) {
      byDate[d][assetKey] = r.nav_usd || 0;
    }
  }

  const result = Object.values(byDate);
  cset(key, result);
  return result;
};

/** ================================
 *  Market Cap and Trading Volume by Asset (from growth_asset table)
 *  ================================ */
export const fetchMarketCapAndVolumeByAsset = async () => {
  const key = ckey("fetchMarketCapAndVolumeByAsset");
  const cached = cget(key);
  if (cached) return cached;

  // Check if underlying data is already cached - avoid async call if possible
  const allDataKey = ckey("fetchAllGrowthAssetData");
  const allDataCached = cget(allDataKey);
  const data = allDataCached ? allDataCached : await fetchAllGrowthAssetData();

  // Transform to chart format: { date, btc_mcap, eth_mcap, sol_mcap, btc_volume, eth_volume, sol_volume }
  const byDate = {};
  for (const r of data || []) {
    const d = r.date;
    if (!byDate[d]) {
      byDate[d] = {
        date: d,
        btc_mcap: 0,
        eth_mcap: 0,
        sol_mcap: 0,
        btc_volume: 0,
        eth_volume: 0,
        sol_volume: 0
      };
    }
    
    if (r.asset === 'BTC') {
      byDate[d].btc_mcap = r.mcap_estimated || 0;
      byDate[d].btc_volume = r.turnover || 0;
    } else if (r.asset === 'ETH') {
      byDate[d].eth_mcap = r.mcap_estimated || 0;
      byDate[d].eth_volume = r.turnover || 0;
    } else if (r.asset === 'SOL') {
      byDate[d].sol_mcap = r.mcap_estimated || 0;
      byDate[d].sol_volume = r.turnover || 0;
    }
  }

  // Convert to array and sort by date
  let result = Object.values(byDate).sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Normalize trading volume data to percentages
  result = result.map(row => {
    const totalVolume = (row.btc_volume || 0) + (row.eth_volume || 0) + (row.sol_volume || 0);
    if (totalVolume > 0) {
      return {
        ...row,
        btc_volume: (row.btc_volume / totalVolume) * 100,
        eth_volume: (row.eth_volume / totalVolume) * 100,
        sol_volume: (row.sol_volume / totalVolume) * 100,
      };
    }
    return row;
  });
  
  // Apply interpolation for weekends
  result = await interpolateWeekendData(result, ['btc_mcap', 'eth_mcap', 'sol_mcap', 'btc_volume', 'eth_volume', 'sol_volume']);
  
  cset(key, result);
  return result;
};

export const fetchSharesOutstandingSnapshot = async (ticker) => {
  const key = ckey("fetchSharesOutstandingSnapshot", ticker);
  const cached = cget(key);
  if (cached) return cached;

  // Use promise cache to prevent concurrent fetches
  return getOrCreatePromise(key, async () => {
    // Query mv_series_latest instead of shares_outstanding_snapshot
    const params = new URLSearchParams({ type: 'snapshot', tickers: ticker, limit: '1000' });
    const data = await fetchCSV(`/api/db?${params.toString()}`);
    const result = Array.isArray(data) && data.length > 0 ? data[0] : null;
    cset(key, result);
    return result;
  });
};